import { Box, ButtonBase, Typography } from "@mui/material";
import { ReactNode } from "react";

export interface CardAction {
  id: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Подсветить действие (например, включённое замораживание) */
  active?: boolean;
}

interface CardActionRowProps {
  actions: CardAction[];
  /**
   * Сетка колонок. По умолчанию — `actions.length` (один ряд).
   * На detail-странице карты передаём `{ xs: actions.length, md: 2 }`,
   * чтобы на десктопе в узкой правой колонке actions рисовались в 2×2.
   */
  columns?: number | { xs?: number; sm?: number; md?: number; lg?: number };
}

/**
 * Строит CSS `grid-template-columns` для MUI-респонсивной записи.
 * `4` → `"repeat(4, 1fr)"`, `{ xs: 4, md: 2 }` → per-breakpoint объект.
 */
function toGridTemplate(
  columns: CardActionRowProps["columns"],
  fallback: number,
): string | Record<string, string> {
  const value = columns ?? fallback;
  if (typeof value === "number") return `repeat(${value}, 1fr)`;
  return Object.fromEntries(
    Object.entries(value).map(([bp, n]) => [bp, `repeat(${n}, 1fr)`]),
  );
}

export function CardActionRow({ actions, columns }: CardActionRowProps) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: toGridTemplate(columns, actions.length),
        gap: "8px",
      }}
    >
      {actions.map((action) => (
        <ButtonBase
          key={action.id}
          disabled={action.disabled}
          onClick={action.onClick}
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            padding: "12px 4px",
            borderRadius: "16px",
            bgcolor: action.active
              ? "rgba(51, 119, 255, 0.16)"
              : theme.palette.mode === "dark"
              ? "rgba(255, 255, 255, 0.04)"
              : "rgba(0, 0, 0, 0.04)",
            border: action.active
              ? "1px solid rgba(51, 119, 255, 0.4)"
              : "1px solid transparent",
            transition: "background 0.15s, transform 0.1s",
            opacity: action.disabled ? 0.45 : 1,
            "&:active": {
              bgcolor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "rgba(0, 0, 0, 0.08)",
              transform: "scale(0.97)",
            },
          })}
        >
          <Box
            sx={(theme) => ({
              width: "40px",
              height: "40px",
              borderRadius: "12px",
              bgcolor: action.active
                ? "rgba(51, 119, 255, 0.28)"
                : theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(0, 0, 0, 0.06)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.primary",
            })}
          >
            {action.icon}
          </Box>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 500,
              color: action.active ? "#8AB4FF" : "text.primary",
              textAlign: "center",
              lineHeight: 1.15,
            }}
          >
            {action.label}
          </Typography>
        </ButtonBase>
      ))}
    </Box>
  );
}
